import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FocusMonitor } from '@angular/cdk/a11y';
import {
  ControlValueAccessor,
  NgControl,
  NgForm,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  extractEmail,
  isAutoAcceptableEmail,
  isValidEmail,
  normalizeEmailCandidate,
  splitEmailList,
} from 'utilities';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';

const noop = (): void => {
  /* required CVA default */
};

const AUTO_ADD_DELAY_MS = 800;

// Keys that commit the current text to a chip - shared by the "add new" input and inline edit.
const COMMIT_KEYS = ['Enter', ',', ';', ' '];

@Component({
  selector: 'ocp-email-chips-input',
  templateUrl: './email-chips-input.component.html',
  styleUrls: ['./email-chips-input.component.scss'],
  imports: [TranslatePipe, ThemedIconComponent],
})
export class EmailChipsInputComponent
  implements ControlValueAccessor, Validator, OnInit, OnDestroy
{
  private static nextId = 0;

  protected readonly ngControl = inject(NgControl, { optional: true, self: true });
  // The surrounding template-driven form, if any. Absent for standalone/reactive usage.
  private readonly parentForm = inject(NgForm, { optional: true });
  private readonly focusMonitor = inject(FocusMonitor);
  readonly placeholder = input('');
  readonly instanceId = `email-chips-${EmailChipsInputComponent.nextId++}`;
  readonly emails = signal<string[]>([]);
  readonly emailsChange = output<string[]>();
  readonly inputValue = signal('');
  readonly disabled = signal(false);

  // Index of the chip currently being edited inline (click-to-edit), or null when none is.
  readonly editingIndex = signal<number | null>(null);
  readonly editValue = signal('');
  private readonly editInputRef = viewChild<ElementRef<HTMLInputElement>>('editInput');
  private readonly fieldRef = viewChild<ElementRef<HTMLElement>>('field');

  readonly showFocusRing = signal(false);

  private readonly invalidEmailError = signal(false);
  // Revealed on form submit, re-hidden on any edit, so a corrected address isn't flagged live.
  private readonly errorsRevealed = signal(false);
  // An invalid inline chip edit; surfaces immediately (unlike the input) and holds the edit open.
  private readonly editError = signal(false);

  // `required` is deliberately not surfaced: the CTA is disabled while empty, so it's never actionable.
  readonly hasError = computed(
    () => this.editError() || (this.errorsRevealed() && this.invalidEmailError())
  );

  // Emails that would be submitted, excluding a chip held open in an invalid edit. The rest are valid.
  readonly validEmails = computed(() => {
    const editing = this.editingIndex();
    if (this.editError() && editing !== null) {
      return this.emails().filter((_, i) => i !== editing);
    }
    return this.emails();
  });

  // Has ≥1 submittable email. Bind a submit control's disabled state here; valid chips keep it enabled.
  readonly canSubmit = computed(() => this.validEmails().length > 0);

  // Any unconfirmed invalid content (leftover input text or an invalid in-progress edit). Hosts
  // should refuse to submit while true and let the revealed error guide the user.
  readonly hasInvalidContent = computed(() => {
    const editing = this.editingIndex();
    if (editing !== null) {
      const edited = normalizeEmailCandidate(this.editValue());
      if (edited !== '' && !isValidEmail(edited)) return true;
    }
    const pending = normalizeEmailCandidate(this.inputValue());
    return pending !== '' && !isValidEmail(pending);
  });

  private onChange: (value: string[]) => void = noop;
  private onTouched: () => void = noop;
  private autoAddTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly boundValidate = this.validate.bind(this);

  constructor() {
    // Must be set synchronously: NgModel/FormControl call setUpControl() before our ngOnInit
    // and throw NG01203 otherwise.
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    // Reveal errors on form submit, so hosts don't have to trigger it explicitly.
    this.parentForm?.ngSubmit.pipe(takeUntilDestroyed()).subscribe(() => this.revealErrors());

    // Focus the edit input and place the cursor at the end as soon as it renders.
    effect(() => {
      if (this.editingIndex() === null) return;
      const inputEl = this.editInputRef()?.nativeElement;
      inputEl?.focus();
      const end = inputEl?.value.length ?? 0;
      inputEl?.setSelectionRange(end, end);
    });

    effect(onCleanup => {
      const fieldEl = this.fieldRef()?.nativeElement;
      if (!fieldEl) return;

      const subscription = this.focusMonitor
        .monitor(fieldEl, true)
        .subscribe(origin => this.showFocusRing.set(origin === 'keyboard'));

      onCleanup(() => {
        subscription.unsubscribe();
        this.focusMonitor.stopMonitoring(fieldEl);
      });
    });
  }

  ngOnInit(): void {
    // `control` is populated by ngOnInit. Add our validator alongside any host-supplied ones
    // (e.g. Validators.required) rather than replacing them.
    const control = this.ngControl?.control;
    if (control) {
      control.addValidators(this.boundValidate);
      this.refreshValidity();
    }
  }

  ngOnDestroy(): void {
    this.clearAutoAddTimeout();
    // Remove our validator so a reused control doesn't retain this instance or duplicate it.
    const control = this.ngControl?.control;
    if (control) {
      control.removeValidators(this.boundValidate);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  validate(): ValidationErrors | null {
    // Unconfirmed invalid text invalidates the control; rendered only once revealed.
    const pending = this.inputValue().trim();
    if (pending && !isValidEmail(pending)) {
      return { invalidEmail: true };
    }
    return null;
  }

  /** Reveals a pending invalid-email error. Auto-invoked on form submit; also callable directly. */
  revealErrors(): void {
    this.errorsRevealed.set(true);
  }

  writeValue(value: string[]): void {
    this.emails.set(value ?? []);
    this.cancelEdit();
    this.refreshValidity();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) {
      this.cancelEdit();
    }
  }

  onInput(value: string): void {
    // Editing re-arms the field: a previously revealed error is hidden until the next submit.
    this.errorsRevealed.set(false);
    this.inputValue.set(value);
    this.refreshValidity();
    this.scheduleAutoAdd();
  }

  onKeydown(event: KeyboardEvent): void {
    if (COMMIT_KEYS.includes(event.key)) {
      event.preventDefault();
      this.clearAutoAddTimeout();
      this.addFromInput();
    }
    if (event.key === 'Backspace' && this.inputValue().length === 0) {
      this.removeEmail(this.emails().length - 1);
    }
  }

  onBlur(): void {
    this.clearAutoAddTimeout();
    this.onTouched();
    this.addFromInput();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';

    for (const part of splitEmailList(text)) {
      const email = extractEmail(part);
      if (email && !this.emails().includes(email)) {
        this.emails.update(list => [...list, email]);
      }
    }

    this.emitChange();
  }

  removeEmail(index: number): void {
    if (index < 0 || index >= this.emails().length) return;
    this.emails.update(list => list.filter((_, i) => i !== index));

    // Keep a pending edit pointed at the right chip once indexes shift.
    const editing = this.editingIndex();
    if (editing === index) {
      this.cancelEdit();
    } else if (editing !== null && editing > index) {
      this.editingIndex.set(editing - 1);
    }

    this.emitChange();
  }

  // Enters inline edit for the chip at `index`. Commit key or blur confirms; Escape cancels.
  protected startEditingChip(index: number): void {
    if (this.disabled()) return;

    this.errorsRevealed.set(false);
    this.editError.set(false);
    this.editingIndex.set(index);
    this.editValue.set(this.emails()[index] ?? '');
  }

  protected onEditInput(value: string): void {
    // Typing clears a shown edit error so the user isn't flagged live while correcting it.
    this.editError.set(false);
    this.editValue.set(value);
  }

  protected onEditKeydown(event: KeyboardEvent, index: number): void {
    if (COMMIT_KEYS.includes(event.key)) {
      event.preventDefault();
      this.commitEdit(index);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  protected onEditBlur(index: number): void {
    this.commitEdit(index);
  }

  // Commits a valid, non-duplicate email. An invalid value surfaces the error and keeps the edit
  // open (never reverts silently); unchanged/empty/duplicate just exit edit mode without an error.
  private commitEdit(index: number): void {
    const newValue = normalizeEmailCandidate(this.editValue());
    const emails = this.emails();
    const original = emails[index];

    if (newValue !== '' && newValue !== original && !isValidEmail(newValue)) {
      this.editError.set(true);
      return;
    }

    const isDuplicateElsewhere = emails.some((email, i) => i !== index && email === newValue);
    if (newValue === original || newValue === '' || isDuplicateElsewhere) {
      this.cancelEdit();
      return;
    }

    this.emails.update(list => list.map((email, i) => (i === index ? newValue : email)));
    this.cancelEdit();
    this.emitChange();
  }

  private cancelEdit(): void {
    this.editingIndex.set(null);
    this.editValue.set('');
    this.editError.set(false);
  }

  // `validate()` reads `inputValue`, not the control value, so force re-validation, then mirror
  // the `invalidEmail` error into a signal `hasError` can react to.
  private refreshValidity(): void {
    this.ngControl?.control?.updateValueAndValidity();
    this.invalidEmailError.set(!!this.ngControl?.hasError('invalidEmail'));
  }

  // Auto-commits the input into a chip once it looks like a complete email, no confirm key needed.
  private scheduleAutoAdd(): void {
    this.clearAutoAddTimeout();
    const pending = this.inputValue().trim();
    if (!pending || !isAutoAcceptableEmail(pending)) {
      return;
    }

    this.autoAddTimeoutId = setTimeout(() => {
      this.autoAddTimeoutId = null;
      if (this.inputValue().trim() === pending) {
        this.addFromInput();
      }
    }, AUTO_ADD_DELAY_MS);
  }

  private clearAutoAddTimeout(): void {
    if (this.autoAddTimeoutId !== null) {
      clearTimeout(this.autoAddTimeoutId);
      this.autoAddTimeoutId = null;
    }
  }

  private addFromInput(): void {
    const raw = normalizeEmailCandidate(this.inputValue());
    if (!raw || !isValidEmail(raw)) {
      this.refreshValidity();
      return;
    }

    if (this.emails().includes(raw)) {
      this.inputValue.set('');
      this.refreshValidity();
      return;
    }

    this.emails.update(list => [...list, raw]);
    this.inputValue.set('');
    this.emitChange();
  }

  private emitChange(): void {
    // Changing the committed list is an edit, so re-arm error display like typing does.
    this.errorsRevealed.set(false);
    const value = this.emails();
    this.onChange(value);
    this.emailsChange.emit(value);
    this.refreshValidity();
  }
}
